import React, { useEffect, useMemo, useState } from 'react';

export default function GradesView({
  gradesWorkspace,
  searchTerm,
  isLoading,
  isSaving,
  saveError,
  onCreateGrade,
  onSaveGrade,
  onDeleteGrade,
  terminology,
  permissions
}) {
  const canWrite = (permissions ?? []).includes('grades.write');
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [draft, setDraft] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState({ name: '' });

  const filteredGrades = useMemo(() => {
    if (!gradesWorkspace) {
      return [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return gradesWorkspace.grades;
    }

    return gradesWorkspace.grades.filter((grade) => (
      grade.name.toLowerCase().includes(normalizedSearch) ||
      String(grade.classCount).includes(normalizedSearch) ||
      String(grade.studentCount).includes(normalizedSearch)
    ));
  }, [gradesWorkspace, searchTerm]);

  useEffect(() => {
    if (!gradesWorkspace) {
      setSelectedGradeId('');
      setDraft(null);
      setIsEditDialogOpen(false);
      setIsCreateDialogOpen(false);
      setCreateDraft({ name: '' });
    }
  }, [gradesWorkspace]);

  useEffect(() => {
    if (!gradesWorkspace) {
      setDraft(null);
      return;
    }

    const selectedGrade = gradesWorkspace.grades.find((grade) => grade.id === selectedGradeId);
    setDraft(selectedGrade ? buildGradeDraft(selectedGrade) : null);
  }, [gradesWorkspace, selectedGradeId]);

  useEffect(() => {
    if (isCreateDialogOpen) {
      setCreateDraft({ name: '' });
    }
  }, [isCreateDialogOpen]);

  useEffect(() => {
    if (!isCreateDialogOpen && !isEditDialogOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        setIsCreateDialogOpen(false);
        setCreateDraft({ name: '' });
        setIsEditDialogOpen(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreateDialogOpen, isEditDialogOpen, isSaving]);

  if (isLoading || !gradesWorkspace) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading grades</h1>
          <p>Preparing the grade configuration workspace.</p>
        </div>
      </section>
    );
  }

  const gradePositionsById = new Map(
    gradesWorkspace.grades.map((grade, index) => [grade.id, index + 1])
  );
  const selectedGrade = gradesWorkspace.grades.find((grade) => grade.id === selectedGradeId) ?? null;
  const selectedGradePosition = selectedGrade ? gradePositionsById.get(selectedGrade.id) ?? 0 : 0;

  async function handleSave(event) {
    event.preventDefault();

    if (!draft || !selectedGrade) {
      return;
    }

    await onSaveGrade(selectedGrade.id, draft);
  }

  async function handleDelete() {
    if (!selectedGrade || isSaving) {
      return;
    }

    const confirmed = window.confirm(
      `Delete the grade "${selectedGrade.name}"? This only works when no ${(terminology?.classes ?? 'classes').toLowerCase()} or ${(terminology?.students ?? 'students').toLowerCase()} still use it.`
    );
    if (!confirmed) {
      return;
    }

    const updatedWorkspace = await onDeleteGrade(selectedGrade.id);
    if (updatedWorkspace) {
      setIsEditDialogOpen(false);
      setSelectedGradeId('');
    }
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();

    const updatedWorkspace = await onCreateGrade(createDraft);
    if (!updatedWorkspace) {
      return;
    }

    const createdGrade = updatedWorkspace.grades.find((grade) => grade.name === createDraft.name.trim());
    setIsCreateDialogOpen(false);
    setCreateDraft({ name: '' });
    setSelectedGradeId(createdGrade?.id ?? updatedWorkspace.grades[0]?.id ?? '');
  }

  return (
    <div className="grades-screen">
      <section className="hero-card roster-hero">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill accent">Grades</span>
            <span className="eyebrow-copy">
              {gradesWorkspace.summary.totalGrades} grade{gradesWorkspace.summary.totalGrades === 1 ? '' : 's'} defined for {gradesWorkspace.project.name}
            </span>
          </div>
          <h1>{gradesWorkspace.project.name}</h1>
          <p>
            Define the grade list before you create {(terminology?.classes ?? 'Classes').toLowerCase()}. {terminology?.class ?? 'Class'} sections and {(terminology?.student ?? 'student').toLowerCase()} grade
            assignments both use these shared project grades.
          </p>
        </div>

        <div className="hero-actions">
          <div className="roster-stat-block">
            <span className="metric-label">Grades</span>
            <strong>{gradesWorkspace.summary.totalGrades}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">{terminology?.classes ?? 'Classes'}</span>
            <strong>{gradesWorkspace.summary.totalClasses}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">{terminology?.students ?? 'Students'}</span>
            <strong>{gradesWorkspace.summary.activeStudents}</strong>
          </div>
        </div>
        {canWrite ? (
          <div className="roster-action-bar">
            <button
              type="button"
              className="primary-button compact-button"
              onClick={() => setIsCreateDialogOpen(true)}
              disabled={isSaving}
            >
              + Add grade
            </button>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">Grade catalog</span>
            <h2>Project grade definitions</h2>
          </div>
          <span className="toolbar-meta">{filteredGrades.length} visible grades</span>
        </div>

        <div className="table-wrap roster-list-wrap">
          <table className="data-grid grades-grid-table">
            <thead>
              <tr>
                <th>Grade</th>
                <th>{terminology?.classes ?? 'Classes'}</th>
                <th>Active {(terminology?.students ?? 'Students').toLowerCase()}</th>
                <th>Order</th>
              </tr>
            </thead>
            <tbody>
              {filteredGrades.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-grid-cell">
                    {searchTerm.trim() ? 'No grades match the current search.' : 'No grades have been defined yet.'}
                  </td>
                </tr>
              ) : filteredGrades.map((grade) => (
                <tr
                  key={grade.id}
                  className={canWrite ? 'clickable-row' : ''}
                  onClick={() => {
                    if (!canWrite) return;
                    setSelectedGradeId(grade.id);
                    setDraft(buildGradeDraft(grade));
                    setIsEditDialogOpen(true);
                  }}
                >
                  <td className="grade-name-cell">{grade.name}</td>
                  <td>{grade.classCount}</td>
                  <td>{grade.studentCount}</td>
                  <td>{gradePositionsById.get(grade.id) ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {isEditDialogOpen && draft && selectedGrade ? (
        <div className="workspace-dialog-backdrop" onClick={() => !isSaving && setIsEditDialogOpen(false)}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-grade-title"
            onClick={(event) => event.stopPropagation()}
            style={{ width: 'min(520px, calc(100vw - 48px))' }}
          >
            <div className="panel-head compact" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span className="panel-kicker">Grade editor</span>
                <h2 id="edit-grade-title">{selectedGrade.name}</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={() => !isSaving && setIsEditDialogOpen(false)}
                aria-label="Close grade editor"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleSave}>
              <div className="grade-form-grid">
                <label className="auth-field">
                  <span>Grade name</span>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(event) => setDraft({ name: event.target.value })}
                    placeholder="Kindergarten"
                    autoFocus
                  />
                </label>
              </div>

              <p className="editor-note">Examples: 1, 2, Kindergarten, Pre-K.</p>

              <div className="roster-related-grid classes-editor-metrics">
                <div className="mini-panel">
                  <span className="mini-label">{terminology?.classes ?? 'Classes'} using grade</span>
                  <strong>{selectedGrade.classCount}</strong>
                  <p>{terminology?.class ?? 'Class'} sections currently assigned to this grade.</p>
                </div>
                <div className="mini-panel">
                  <span className="mini-label">Active {(terminology?.students ?? 'Students').toLowerCase()}</span>
                  <strong>{selectedGrade.studentCount}</strong>
                  <p>{terminology?.student ?? 'Student'} records currently assigned to this grade.</p>
                </div>
              </div>

              <div className="roster-form-actions">
                {canWrite ? (
                  <button
                    type="button"
                    className="utility-button danger-button"
                    onClick={handleDelete}
                    disabled={isSaving}
                  >
                    Delete grade
                  </button>
                ) : null}
                <button type="submit" className="primary-button" disabled={isSaving || !canWrite}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Saving grade' : 'Save grade'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isCreateDialogOpen ? (
        <div className="workspace-dialog-backdrop" onClick={() => !isSaving && setIsCreateDialogOpen(false)}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-grade-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">New grade</span>
                <h2 id="create-grade-title">Add grade definition</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={() => {
                  if (!isSaving) {
                    setIsCreateDialogOpen(false);
                    setCreateDraft({ name: '' });
                  }
                }}
                aria-label="Close add grade dialog"
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form" onSubmit={handleCreateSubmit}>
              <div className="grade-form-grid">
                <label className="auth-field">
                  <span>Grade name</span>
                  <input
                    type="text"
                    value={createDraft.name}
                    onChange={(event) => setCreateDraft({ name: event.target.value })}
                    placeholder="Kindergarten"
                    autoFocus
                  />
                </label>
              </div>

              <p className="editor-note">Examples: 1, 2, Kindergarten, Pre-K.</p>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={() => setCreateDraft({ name: '' })}
                  disabled={isSaving}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="utility-button"
                  onClick={() => {
                    if (!isSaving) {
                      setIsCreateDialogOpen(false);
                      setCreateDraft({ name: '' });
                    }
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving || !canWrite}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Saving grade' : 'Create grade'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildGradeDraft(grade) {
  return {
    name: grade.name
  };
}
